import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Loader2, Minus, UserCheck, X } from "lucide-react";
import Modal from "components/common/Modal";
import betaAPI from "v2/api";
import { dateLocale } from "i18n";
import { showToast } from "utils/toast";
import ConfirmDialog from "../common/ConfirmDialog";

const NEW_PLAYER = "__new__";

const ageOn = (iso, today = new Date()) => {
  const dob = new Date(`${iso}T00:00:00`);
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age -= 1;
  return age;
};

const formatDob = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString(dateLocale("en-GB"), {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

const ConsentChip = ({ granted, label }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ${
      granted ? "bg-[#ECFDF5] text-[#047857]" : "bg-[#F3F4F6] text-[#6B7280]"
    }`}
  >
    {granted ? <Check size={12} /> : <Minus size={12} />}
    {label}
  </span>
);

const ClaimCard = ({
  claim,
  showResearch,
  rosterOptions,
  busy,
  onApprove,
  onReject,
}) => {
  const { t } = useTranslation();
  const [choice, setChoice] = useState("");

  return (
    <li className="rounded-xl border border-[#E5E7EB] p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <p className="text-base font-semibold text-[#111827]">
          {claim.child_name}
        </p>
        <p className="text-sm text-[#6B7280]">
          {t("team.guardianClaims.born", {
            date: formatDob(claim.child_date_of_birth),
            age: ageOn(claim.child_date_of_birth),
          })}
        </p>
      </div>
      <p className="mt-1 text-sm text-[#4B5563]">
        {t("team.guardianClaims.parent", {
          name: claim.guardian_name,
          email: claim.guardian_email,
        })}
      </p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        <ConsentChip
          granted={claim.consents.club_tracking}
          label={t("team.guardianClaims.consents.clubTracking")}
        />
        {showResearch && (
          <ConsentChip
            granted={claim.consents.research}
            label={t("team.guardianClaims.consents.research")}
          />
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <select
          value={choice}
          onChange={(e) => setChoice(e.target.value)}
          disabled={busy}
          aria-label={t("team.guardianClaims.matchLabel", {
            child: claim.child_name,
          })}
          className="min-w-0 flex-1 rounded-[10px] border border-[#E5E7EB] bg-white px-3 py-2.5 text-sm text-[#111827] outline-none focus:border-[#E07A3A]"
        >
          <option value="">{t("team.guardianClaims.choosePlayer")}</option>
          {rosterOptions.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
          <option value={NEW_PLAYER}>
            {t("team.guardianClaims.addNew", { child: claim.child_name })}
          </option>
        </select>
        <button
          onClick={() =>
            onApprove(
              claim,
              choice === NEW_PLAYER
                ? { new_player: { name: claim.child_name } }
                : { player_id: choice }
            )
          }
          disabled={!choice || busy}
          className="inline-flex items-center gap-1.5 rounded-[10px] bg-gradient-to-br from-[#E8854A] to-[#D66B2E] px-4 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#F3F4F6] disabled:bg-none disabled:text-[#9CA3AF]"
        >
          <UserCheck size={16} />
          {t("team.guardianClaims.approve")}
        </button>
        <button
          onClick={() => onReject(claim)}
          disabled={busy}
          className="rounded-[10px] px-3 py-2.5 text-sm font-medium text-[#DC2626] hover:bg-[#FEF2F2] disabled:opacity-60"
        >
          {t("team.guardianClaims.reject")}
        </button>
      </div>
    </li>
  );
};

/**
 * Parent sign-ups waiting for the coach. Each one is matched to a player on
 * the roster by hand (names won't match exactly), added as a new player, or
 * rejected. Only matched players can wear a sensor.
 */
const GuardianClaimsModal = ({
  show,
  onClose,
  workspaceId,
  teamId,
  players,
  onChanged,
}) => {
  const { t } = useTranslation();
  const [claims, setClaims] = useState(null);
  // Older API responses don't say; assume research is asked for.
  const [showResearch, setShowResearch] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [rejecting, setRejecting] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await betaAPI.getGuardianClaims({ workspaceId, teamId });
      setClaims(data.claims);
      setShowResearch(!data.purposes || data.purposes.includes("research"));
    } catch (err) {
      console.error("Failed to load parent sign-ups", err);
      showToast.error(t("team.guardianClaims.toasts.loadFailed"));
      setClaims([]);
    }
  }, [workspaceId, teamId, t]);

  useEffect(() => {
    if (show) {
      setClaims(null);
      load();
    }
  }, [show, load]);

  // Only players still waiting for a parent can be matched (one per child).
  const rosterOptions = (players || []).filter(
    (p) => p.consentStatus === "awaiting"
  );

  const done = (claim) => {
    setClaims((prev) => prev.filter((c) => c.id !== claim.id));
    onChanged?.();
  };

  const approve = async (claim, body) => {
    setBusyId(claim.id);
    try {
      const { data } = await betaAPI.approveGuardianClaim({
        workspaceId,
        teamId,
        claimId: claim.id,
        body,
      });
      showToast.success(
        t("team.guardianClaims.toasts.approved", { player: data.player.name })
      );
      done(claim);
    } catch (err) {
      console.error("Failed to approve sign-up", err);
      showToast.error(
        err.payload?.code === "player_has_guardian"
          ? t("team.guardianClaims.toasts.playerHasGuardian")
          : t("team.guardianClaims.toasts.approveFailed")
      );
    } finally {
      setBusyId(null);
    }
  };

  const reject = async () => {
    const claim = rejecting;
    setRejecting(null);
    setBusyId(claim.id);
    try {
      await betaAPI.rejectGuardianClaim({
        workspaceId,
        teamId,
        claimId: claim.id,
      });
      showToast.success(t("team.guardianClaims.toasts.rejected"));
      done(claim);
    } catch (err) {
      console.error("Failed to reject sign-up", err);
      showToast.error(t("team.guardianClaims.toasts.rejectFailed"));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Modal show={show} onClose={onClose} maxWidth="640px">
      <div className="flex items-start justify-between gap-4 border-b border-[#F3F4F6] px-7 pb-5 pt-6">
        <div>
          <h2 className="text-lg font-bold text-[#111827]">
            {t("team.guardianClaims.title")}
          </h2>
          <p className="mt-1 text-sm text-[#6B7280]">
            {t("team.guardianClaims.subtitle")}
          </p>
        </div>
        <button
          onClick={onClose}
          aria-label={t("common.actions.close")}
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] text-[#6B7280] hover:bg-[#F3F4F6]"
        >
          <X size={18} />
        </button>
      </div>

      <div className="max-h-[65vh] overflow-y-auto px-7 py-6">
        {claims === null ? (
          <div className="flex justify-center py-8">
            <Loader2 size={24} className="animate-spin text-[#E07A3A]" />
          </div>
        ) : claims.length === 0 ? (
          <p className="py-6 text-center text-[15px] text-[#6B7280]">
            {t("team.guardianClaims.empty")}
          </p>
        ) : (
          <ul className="space-y-3">
            {claims.map((claim) => (
              <ClaimCard
                key={claim.id}
                claim={claim}
                showResearch={showResearch}
                rosterOptions={rosterOptions}
                busy={busyId === claim.id}
                onApprove={approve}
                onReject={setRejecting}
              />
            ))}
          </ul>
        )}
      </div>

      <ConfirmDialog
        open={!!rejecting}
        title={t("team.guardianClaims.confirmReject.title", {
          child: rejecting?.child_name,
        })}
        body={t("team.guardianClaims.confirmReject.body", {
          name: rejecting?.guardian_name,
        })}
        confirmLabel={t("team.guardianClaims.confirmReject.confirm")}
        danger
        onConfirm={reject}
        onCancel={() => setRejecting(null)}
      />
    </Modal>
  );
};

export default GuardianClaimsModal;
